import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Eye } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";
import { PageHeader } from "@shared/ui/pageHeader";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { Switch } from "@shared/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { SharedFormArray } from "@shared/components/SharedFormArray";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { usePostMeterReadingFormat, usePatchMeterReadingFormat, useMeterReadingFormatDetail, useGetReadFormatFields } from "../../../hooks";
import { useGetUtilityServices, useGetMeterReadMethods, useOems, useGetResourceTypeList, useGetResourceSubtypeList } from "@features/mx/hooks";

interface MobileSection {
  title: string;
  key: string;
  enabled: boolean;
  collapsed: boolean;
  fields: { label: string; type: string; key: string; enabled: boolean }[];
}

export function Create() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: formatDetail } = useMeterReadingFormatDetail(id || "", remoteUtilityId);
  const { mutate: createFormat } = usePostMeterReadingFormat();
  const { mutate: updateFormat } = usePatchMeterReadingFormat();
  const { data: sections } = useGetReadFormatFields(remoteUtilityId);
  const { data: servicesData } = useGetUtilityServices(remoteUtilityId);

  const utilityServiceOptions = React.useMemo(() => {
    return Array.isArray(servicesData)
      ? servicesData
        .filter((item: any) => item.isActive === true)
        .map((item: any) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [servicesData]);
  const { data: readMethodsData } = useGetMeterReadMethods(remoteUtilityId);

  const readMethodOptions = React.useMemo(() => {
    return Array.isArray(readMethodsData)
      ? readMethodsData
        .filter((item: any) => item.isActive === true)
        .map((item: any) => ({
          label: item.name,
          value: item.id,
        }))
      : [];
  }, [readMethodsData]);

  const { data: oemsData } = useOems(remoteUtilityId);

  const oemOptions = React.useMemo(() => {
    const list = Array.isArray(oemsData) ? oemsData : (oemsData?.results || oemsData?.result || []);
    return Array.isArray(list)
      ? list.map((item: any) => ({
        label: item.organisationName,
        value: item.id,
      }))
      : [];
  }, [oemsData]);

  const [watchResourceType, setWatchResourceType] = React.useState<string>("");

  const { data: resourceTypeResponse } = useGetResourceTypeList(remoteUtilityId);
  const { data: subtypesResponse } = useGetResourceSubtypeList(
    remoteUtilityId,
    watchResourceType ? [watchResourceType] : undefined
  );

  const resourceTypeOptions = React.useMemo(() => {
    const results = resourceTypeResponse?.result || [];
    return results.map((item: any) => ({
      label: item.name,
      value: item.code,
    }));
  }, [resourceTypeResponse]);

  const subtypesList = React.useMemo(() => {
    const results = subtypesResponse?.result || [];
    return results.map((item: any) => ({
      label: item.name,
      value: item.code,
    }));
  }, [subtypesResponse]);

  const getReadMethodName = React.useCallback((val: any) => {
    if (!val) return "";
    const found = readMethodsData?.find((item: any) => String(item.id) === String(val) || item.name === val);
    return found ? found.name : val;
  }, [readMethodsData]);


  const [mobileSections, setMobileSections] = useState<MobileSection[]>([]);

  const mapFieldType = (type: string) => {
    const upper = (type || "").toUpperCase();
    if (upper === "NUMERIC") return "Number";
    return upper.charAt(0) + upper.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (sections && Array.isArray(sections)) {
      const filtered = sections.filter((sec: any) => sec.isNewV1 === true && !sec.isMandatorySection);
      const mapped = filtered.map((sec: any) => {
        let isSectionEnabled = true;
        let savedFields: any[] = [];

        if (id && formatDetail) {
          const savedSectionsArray = formatDetail.sections || [];
          const savedSection = savedSectionsArray.find((s: any) => s.section === sec.section);

          if (savedSection) {
            isSectionEnabled = true;
            savedFields = savedSection.fields || [];
          } else if (formatDetail.configurationJson) {
            savedFields = Object.values(formatDetail.configurationJson).filter((f: any) => f.section === sec.section);
            isSectionEnabled = savedFields.length > 0;
          } else {
            isSectionEnabled = false;
          }
        }

        return {
          title: sec.section,
          key: sec.sectionKey || sec.section_key || "",
          enabled: id ? isSectionEnabled : true,
          collapsed: true,
          fields: sec.fields ? sec.fields.map((f: any) => {
            const isFieldEnabled = id && savedFields.length > 0
              ? savedFields.some((sf: any) => sf.key === f.key || sf.field_name === (f.fieldName || f.label))
              : false;

            return {
              label: f.fieldName || f.label || "",
              type: f.fieldType ? mapFieldType(f.fieldType) : "Text",
              key: f.key || "",
              enabled: isFieldEnabled,
            };
          }) : [],
        };
      });
      setMobileSections(mapped);

      if (!id) {
        const mandatoryFields = sections
          .filter((sec: any) => sec.isMandatorySection === true && sec.isNewV1 === true)
          .flatMap((sec: any) =>
            (sec.fields || []).map((f: any) => ({
              source: "Fixed",
              label: f.fieldName || f.label || "",
              type: f.fieldType ? f.fieldType.toUpperCase() : "TEXT",
              required: f.required !== undefined ? f.required : true,
              key: f.key,
              section: sec.section,
              section_key: sec.sectionKey || sec.section_key,
            }))
          );
        if (mandatoryFields.length > 0) {
          form.setValue("formatFields", mandatoryFields);
        }
      }
    }
  }, [sections, id, formatDetail]);

  const initialValues = {
    formatName: "",
    utilityService: "",
    resourceType: "",
    resourceSubType: "",
    dataCollectionMethod: "",
    vendor: "",
    // formatFields: [
    //   { source: "Fixed", label: "Current Read", type: "Number", required: true, section: "Format Fields", section_key: "format_fields" },
    //   { source: "Fixed", label: "Read Date", type: "Date", required: true, section: "Format Fields", section_key: "format_fields" },
    // ],
  };

  const formFields: FormField[] = [
    {
      name: "formatName",
      label: "Format Name",
      type: "text",
      required: true,
      placeholder: "e.g. Monthly Water Reading",
      className: "col-span-6",
    },
    {
      name: "utilityService",
      label: "Utility Service",
      type: "select",
      required: true,
      placeholder: "Select utility service",
      options: utilityServiceOptions,
      className: "col-span-6",
    },
    {
      name: "resourceType",
      label: "Resource Type",
      type: "select",
      required: false,
      placeholder: "Select resource type",
      options: resourceTypeOptions,
      className: "col-span-6 mt-4",
    },
    {
      name: "resourceSubType",
      label: "Resource Sub Type",
      type: "select",
      required: false,
      placeholder: "select a type first",
      disabled: (formValues) => !formValues.resourceType,
      options: subtypesList,
      className: "col-span-6 mt-4",
    },
    {
      name: "dataCollectionMethod",
      label: "Read Method",
      type: "select",
      required: true,
      placeholder: "Select method",
      options: readMethodOptions,
      className: "col-span-6 mt-4",
    },
    {
      name: "vendor",
      label: "Vendor",
      type: "select",
      required: false,
      placeholder: "Select vendor",
      showWhen: (formValues) => getReadMethodName(formValues.dataCollectionMethod)?.toLowerCase() === "automated",
      options: oemOptions,
      helperText: "Selecting a vendor auto-populates its standard fields below.",
      className: "col-span-6 mt-4",
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: initialValues,
  });

  const currentResourceType = form.watch("resourceType");
  useEffect(() => {
    setWatchResourceType(currentResourceType || "");
  }, [currentResourceType]);

  useEffect(() => {
    if (id && formatDetail) {
      const sectionsArray = formatDetail.sections || [];
      const formatSection = sectionsArray.find((s: any) => s.section === "Format Fields");

      const rawFields = formatSection?.fields ||
        (formatDetail.configurationJson ? Object.values(formatDetail.configurationJson).filter((f: any) => f.section === "Format Fields") : []) ||
        formatDetail.formatFields ||
        [];

      console.log('raw fields', rawFields, formatDetail)

      const mappedFields = rawFields
        .sort((a: any, b: any) => (a.sequenceId || 0) - (b.sequenceId || 0))
        .map((f: any) => {
          const label = f.label || f.fieldName || f.field_name || "";
          return {
            source: f.source,
            label: label,
            type: f.fieldType ? f.fieldType.toUpperCase() : "TEXT",
            required: f.required !== undefined ? f.required : (f.isRequired !== undefined ? f.isRequired : (f.is_required !== undefined ? f.is_required : false)),
            options: f.options || f.values || [],
            section: f.section || undefined,
            section_key: f.sectionKey || f.section_key || undefined,
            key: f.key,
          };
        });

      console.log('mapped fieldss', mappedFields)

      form.reset({
        formatName: formatDetail.name || "",
        utilityService: formatDetail.utilityService || "",
        resourceType: formatDetail.resourceType || "",
        resourceSubType: formatDetail.resourceSubType || "",
        dataCollectionMethod: formatDetail.dataCollectionMethod?.id || "",
        vendor: (getReadMethodName(formatDetail.dataCollectionMethod?.id)?.toLowerCase() === "automated") ? (formatDetail.vendor || "") : "",
        formatFields: mappedFields,
      });
    }
  }, [id, formatDetail]);

  const watchMethod = form.watch("dataCollectionMethod");
  console.log('getReadMethodName', getReadMethodName(watchMethod))

  const watchVendor = form.watch("vendor");

  const isAutomated = React.useMemo(() => {
    return getReadMethodName(watchMethod)?.toLowerCase() === "automated";
  }, [watchMethod]);

  const selectedVendor = React.useMemo(() => {
    const list = Array.isArray(oemsData) ? oemsData : (oemsData?.results || oemsData?.result || []);
    return list.find((item: any) => String(item.id) === String(watchVendor));
  }, [oemsData, watchVendor]);

  const vendorDetails = React.useMemo(() => {
    if (!selectedVendor) return null;
    const name = selectedVendor.organisationName || watchVendor;
    return {
      name: selectedVendor.organisationName || name,
      protocol: selectedVendor.protocol || selectedVendor.smartMeterInfo?.protocol || "N/A",
      website: selectedVendor.companyWebsite || "N/A",
      email: selectedVendor.email || "N/A",
      phone: selectedVendor.landlineNumber || "N/A",
      description: selectedVendor.description || "N/A",
    };
  }, [selectedVendor, watchVendor]);

  useEffect(() => {
    let currentFields = form.getValues("formatFields") || [];
    if (currentFields.length === 0) {
      currentFields = [
        { source: "Fixed", label: "Current Read", type: "NUMERIC", required: true },
        { source: "Fixed", label: "Read Date", type: "DATE", required: true },
      ];
    }
    // Filter out existing vendor fields
    currentFields = currentFields.filter((f: any) => f.source !== "Vendor");

    if (isAutomated && selectedVendor) {
      const apiFields = selectedVendor.smartMeterInfo?.fields || selectedVendor.smartMeterInfo?.customFields || [];
      const vendorFields = Array.isArray(apiFields)
        ? apiFields.map((vf: any) => ({
          source: "Vendor" as const,
          label: vf.fieldLabel || vf.label || vf.field_label || "",
          type: vf.type ? vf.type.toUpperCase() : "TEXT",
          required: vf.isRequired !== undefined ? vf.isRequired : (vf.required !== undefined ? vf.required : false),
          options: vf.options || [],
        }))
        : [];
      form.setValue("formatFields", [...vendorFields, ...currentFields]);
    } else {
      if (!isAutomated && watchVendor) {
        form.setValue("vendor", "");
      }
      form.setValue("formatFields", currentFields);
    }
  }, [isAutomated, watchVendor, selectedVendor]);



  const handleAddCustomField = () => {
    const currentFields = form.getValues("formatFields") || [];
    const existingSectionField = currentFields.find((f: any) => f.section && f.section_key);
    const mandatorySection = sections?.find((sec: any) => sec.isMandatorySection === true && sec.isNewV1 === true);
    const secVal = existingSectionField?.section || mandatorySection?.section || "Format Fields";
    const secKeyVal = existingSectionField?.section_key || mandatorySection?.sectionKey || mandatorySection?.section_key || "format_fields";

    const newField = {
      source: "Custom" as const,
      label: "",
      type: "TEXT",
      required: false,
      section: secVal,
      section_key: secKeyVal,
    };
    form.setValue("formatFields", [...currentFields, newField]);
    toast({ title: "success", description: "blank custom field added" });
  };

  const itemFields: FormField[] = [
    {
      name: "source",
      label: "Source",
      type: "custom",
      customRender: (field, formInstance, onChange, value) => {
        const sourceVal = value || "Custom";
        const isFix = sourceVal === "Fixed";
        const isSys = sourceVal === "System";
        const isVdr = sourceVal === "Vendor";

        return (
          <div className="flex items-center h-10">
            <span
              className={`px-2.5 py-0.5 rounded text-xs font-bold ${isFix
                ? "bg-amber-100 text-amber-700"
                : isSys
                  ? "bg-purple-100 text-purple-700"
                  : isVdr
                    ? "bg-blue-100 text-blue-700"
                    : "bg-green-100 text-green-700"
                }`}
            >
              {sourceVal}
            </span>
          </div>
        );
      },
    },
    {
      name: "label",
      label: "Field Label",
      type: "text",
      required: true,
      placeholder: "e.g. Meter Reading",
      readonly: (row) => row.source === "Fixed" || row.source === "System",
    },
    {
      name: "type",
      label: "Field Type",
      type: "select",
      required: true,
      getOptions: (row) => {
        const baseOptions = [
          { label: "Text", value: "TEXT" },
          { label: "Number", value: "NUMERIC" },
          { label: "Date", value: "DATE" },
          { label: "Time", value: "TIME" },
          { label: "Photo", value: "PHOTO" },
        ];
        if (row.source === "System" || row.source === "Fixed" || row.source === "Vendor") {
          return [...baseOptions, { label: "Dropdown", value: "DROPDOWN" }];
        }
        return baseOptions;
      },
      readonly: (row) => row.source === "Fixed" || row.source === "System",
    },
    {
      name: "required",
      label: "Req.",
      type: "switch",
      disabled: (row) => row.source === "Fixed",
    },
  ];

  const toggleSection = (index: number) => {
    setMobileSections((prev) =>
      prev.map((s, idx) => (idx === index ? { ...s, enabled: !s.enabled } : s))
    );
  };

  const toggleSectionCollapse = (index: number) => {
    setMobileSections((prev) =>
      prev.map((s, idx) => (idx === index ? { ...s, collapsed: !s.collapsed } : s))
    );
  };

  const toggleSectionField = (secIndex: number, fieldIndex: number) => {
    setMobileSections((prev) =>
      prev.map((s, idx) => {
        if (idx !== secIndex) return s;
        return {
          ...s,
          fields: s.fields.map((f, fIdx) =>
            fIdx === fieldIndex ? { ...f, enabled: !f.enabled } : f
          ),
        };
      })
    );
  };

  const handleCreateFormat = (data: any) => {
    let seqId = 1;
    const isMobile = getReadMethodName(data.dataCollectionMethod)?.toLowerCase() === "mobile";

    const mobileFieldsJson: any = {};
    if (isMobile) {
      // Add enabled fields from mobileSections
      mobileSections.forEach((section) => {
        if (section.enabled) {
          section.fields.forEach((field) => {
            if (field.enabled) {
              const fieldKey = field.label;
              const inputTypeUpper = "AUTOMATED";
              const fieldTypeUpper = (field.type || "Text").toUpperCase() === "NUMBER" ? "NUMERIC" : (field.type || "Text").toUpperCase();

              mobileFieldsJson[fieldKey] = {
                field_name: field.label,
                sequence_id: seqId++,
                key: field.key,
                input_type: inputTypeUpper,
                field_type: fieldTypeUpper,
                display_type: "STATIC",
                required: false,
                is_active: true,
                section: section.title,
                section_key: section.key,
                source: "System",
              };
            }
          });
        }
      });
    }

    const formatFieldsJson: any = {};
    // Add formatFields (fixed / custom)
    const formatFields = form.getValues("formatFields") || [];
    const representativeField = formatFields.find((f: any) => f.section && f.section_key);
    const mandatorySection = sections?.find((sec: any) => sec.isMandatorySection === true && sec.isNewV1 === true);
    const fallbackSection = representativeField?.section || mandatorySection?.section || "Format Fields";
    const fallbackSectionKey = representativeField?.section_key || mandatorySection?.sectionKey || mandatorySection?.section_key || "format_fields";

    formatFields.forEach((field: any) => {
      const fieldKey = field.label;
      if (!fieldKey) return;
      const inputTypeUpper = "MANUAL";
      const fieldTypeUpper = (field.type || "Text").toUpperCase() === "NUMBER" ? "NUMERIC" : (field.type || "Text").toUpperCase();

      formatFieldsJson[fieldKey] = {
        field_name: field.label,
        sequence_id: seqId++,
        key: field.key || (field.label || "").replace(/\s+/g, "_").toLowerCase(),
        input_type: inputTypeUpper,
        field_type: fieldTypeUpper,
        display_type: "UI",
        required: Boolean(field.required),
        is_active: true,
        section: field.section || fallbackSection,
        section_key: field.section_key || fallbackSectionKey,
        source: field.source || "Custom"
      };
    });

    const configurationJson = {
      ...(isMobile ? mobileFieldsJson : {}),
      ...formatFieldsJson,
    };

    const payload = {
      name: data.formatName,
      utility_service: data.utilityService,
      resource_type: data.resourceType || "",
      resource_sub_type: data.resourceSubType || "",
      data_collection_method: data.dataCollectionMethod,
      meter_type: 1,
      vendor: data.vendor || "",
      configuration_json: configurationJson,
      remote_utility_id: remoteUtilityId,
    };

    if (id) {
      updateFormat(
        { id: Number(id), payload },
        {
          onSuccess: () => {
            navigate("/mx/settings?tab=read-format&subtab=format");
          },
        }
      );
    } else {
      createFormat(payload, {
        onSuccess: () => {
          navigate("/mx/settings?tab=read-format&subtab=format");
        },
      });
    }
  };

  return (
    <div className="space-y-6 p-4">
      <PageHeader
        title={id ? "Update Format" : "Create New Format"}
        description="Define a reading format with custom fields"
        onBack={() => navigate("/mx/settings?tab=read-format&subtab=format")}
      />

      {/* Format Details Card */}
      <div className="bg-white rounded-xl border border-gray-150 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Format Details</h3>
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={() => { }}
          gridColumns={12}
          config={{
            showSubmitButton: false,
            classes: FIELD_CLASSES,
          }}
        />
      </div>

      {/* Vendor Details Card */}
      {isAutomated && watchVendor && vendorDetails && (
        <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-6 shadow-sm space-y-4">
          <h3 className="text-md font-bold">Vendor Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-xs text-gray-400 block font-semibold">Vendor Name</span>
              <span className="text-gray-800 font-medium">{vendorDetails.name}</span>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold">Protocol</span>
              <span className="text-gray-800 font-medium">{vendorDetails.protocol}</span>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold">Website</span>
              <a
                href={vendorDetails.website && vendorDetails.website !== "N/A" ? `https://${vendorDetails.website}` : undefined}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline font-medium"
              >
                {vendorDetails.website}
              </a>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold">Support Email</span>
              <span className="text-gray-800 font-medium">{vendorDetails.email}</span>
            </div>
            <div>
              <span className="text-xs text-gray-400 block font-semibold">Support Phone</span>
              <span className="text-gray-800 font-medium">{vendorDetails.phone}</span>
            </div>
            <div className="col-span-2 md:col-span-3">
              <span className="text-xs text-gray-400 block font-semibold">Description</span>
              <p className="text-gray-600 mt-0.5">{vendorDetails.description}</p>
            </div>
          </div>
        </div>
      )}



      {getReadMethodName(watchMethod)?.toLowerCase() === "mobile" && (
        <div className="rounded-xl border border-grey-100 p-6 shadow-sm space-y-6">
          <div>
            <h3 className="text-lg font-bold">Details Sections</h3>
            <p className="text-sm text-gray-500 mt-1">
              Enable the sections and fields that should appear on the reading form.
            </p>
          </div>
          <div className="space-y-4">
            {mobileSections.map((section, secIdx) => {
              const enabledCount = section.fields.filter((f) => f.enabled).length;
              const totalCount = section.fields.length;
              return (
                <div
                  key={section.title}
                  className={`bg-white rounded-xl border border-gray-150 shadow-sm overflow-hidden transition-all ${!section.enabled ? "opacity-60" : ""
                    }`}
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between p-4 bg-gray-50/50 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <h4 className="font-bold text-gray-800 text-sm">{section.title}</h4>
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200/60 text-gray-700">
                        {enabledCount} / {totalCount} selected
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                        Auto-populated
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 font-medium">Enable Section</span>
                        <Switch checked={section.enabled} onCheckedChange={() => toggleSection(secIdx)} />
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleSectionCollapse(secIdx)}
                        className="p-1 hover:bg-gray-200 rounded text-gray-600"
                      >
                        {section.collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Section Fields Body */}
                  {!section.collapsed && section.enabled && (
                    <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white">
                      {section.fields.map((field, fIdx) => (
                        <div
                          key={field.label}
                          className="p-4 rounded-lg border border-gray-150 bg-white hover:border-gray-300 transition-all flex items-center justify-between shadow-sm"
                        >
                          <div className="flex items-center gap-2.5">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <div>
                              <span className="text-sm font-semibold text-gray-800 block leading-tight">{field.label}</span>
                              <span className="text-xs text-blue-600 font-medium block mt-0.5">{field.type}</span>
                            </div>
                          </div>
                          <Switch
                            checked={field.enabled}
                            onCheckedChange={() => toggleSectionField(secIdx, fIdx)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Field Builder Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Format Fields</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure custom fields for this reading format.
            </p>
          </div>
          <div className="flex gap-3">


            <Button
              type="button"
              variant="outline"
              onClick={handleAddCustomField}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              + Add Custom Field
            </Button>
          </div>
        </div>

        <SharedFormArray
          name="formatFields"
          form={form}
          itemFields={itemFields}
          variant="table"
          addButtonText=""
          showAddButton={false}
          reorderable={true}
          gridTemplateColumns="48px 120px 1.5fr 1.2fr 100px 50px"
          isDeletable={(item) => item?.source !== "Fixed"}
          renderAfterContent={(index) => {
            const row = form.getValues(`formatFields.${index}`);
            if (row?.type === "DROPDOWN" && row.options && row.options.length > 0) {
              return (
                <div className="flex flex-wrap items-center gap-1.5 pt-1 pl-[140px] pb-2">
                  <span className="text-xs text-gray-450 font-medium mr-1">Predefined Options:</span>
                  {row.options.map((opt: string) => (
                    <span
                      key={opt}
                      className="px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-100"
                    >
                      {opt}
                    </span>
                  ))}
                </div>
              );
            }
            return null;
          }}
        />
      </div>

      {/* Bottom Footer Actions */}
      <div className="flex justify-end gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/mx/settings?tab=read-format&subtab=format")}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={form.handleSubmit(handleCreateFormat)}
          disabled={!form.formState.isValid}
          className="bg-primary text-white"
        >
          {id ? "Update Format" : "Create Format"}
        </Button>
      </div>
    </div>
  );
}

export default Create;